import { useEffect, useState, useContext } from "react";
import { motion } from "framer-motion";
import { ClipboardList, Trash2 } from "lucide-react";
import {
  GenericTable,
  Spinner,
  Pagination,
  FilterCard,
  RoleValidator,
} from "../../../Components/Common";
import { formatDateDDMMYYYY } from "../../../Utils";
import { toProperCase } from "../../../Utils";
import { useNavigate } from "react-router-dom";
import { CREATE_MRR, EDIT_MRR, VIEW_DETAILED_MRR } from "../../../routes"; // define this route
import toast from "react-hot-toast";
import {
  getMRRStatusColor,
  MATERIAL_COLUMNS,
  MATERIAL_LABELS,
  MRR_STATUS,
  MRR_STATUS_VALUE_LABELS,
  TOAST_MESSAGES,
} from "./constants";
import { getAllWarehouses } from "../../../Services/inventoryService";
import { extractErrorMessage } from "../../../Utils/extractErrorMessage";
import { MdAdd, MdModeEditOutline } from "react-icons/md";
import { FiInfo } from "react-icons/fi";
import { useMRR } from "../Hooks/useMRR";
import { getPriorityStatusColor, PRIORITY_OPTIONS, ROLES } from "../../../Constants/Strings";
import { UserContext } from "../../../Context/UserContext";

const MaterialListPage = () => {
  const {
    loading,
    filters,
    setFilters,
    page,
    totalPages,
    rowsPerPage,
    onPageChange,
    onPageSizeChange,
    fetchAllMRRs,
    deleteMRR,
  } = useMRR();

  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const role = user?.role;

  // Dummy Material Requisition data
  const [materials, setMaterials] = useState([]);
  const [warehouses, setWarehouses] = useState([]);

  const fetchSiteWarehouses = async () => {
    try {
      const response = await getAllWarehouses({ limit: 0, is_site: true });
      if (response.status === 200) {
        const options = response.data.map((wh) => ({
          value: wh.id,
          label: wh.name,
        }));
        setWarehouses([{ value: "", label: "All" }, ...options]);
      } else {
        const msg =
          extractErrorMessage(response) ||
          TOAST_MESSAGES.FAILED_TO_FETCH_WAREHOUSES;
        toast.error(msg);
      }
    } catch (error) {
      toast.error(
        extractErrorMessage(error) || TOAST_MESSAGES.FAILED_TO_FETCH_WAREHOUSES
      );
    }
  };

  useEffect(() => {
    fetchSiteWarehouses();
  }, []);

  const loadMRRs = async () => {
    const data = await fetchAllMRRs();
    if (data) setMaterials(data);
    else setMaterials([]);
  };

  useEffect(() => {
    loadMRRs();
  }, [page, rowsPerPage, filters]);

  const handleDeleteMaterial = async (mrrItem) => {
    const data = await deleteMRR(mrrItem.id);
    if (data) {
      setMaterials((prev) => prev.filter((item) => item.id !== mrrItem.id));
    }
  };

  // Render Field Values for Table
  const renderFields = (key, rowData) => {
    switch (key) {
      case "estimated_delivery_date":
        return formatDateDDMMYYYY(rowData.estimated_delivery_date);
      case "created": {
        const date = rowData.created.split("T")[0];
        return formatDateDDMMYYYY(date);
      }
      case "status":
        return (
          <span
            className={`px-1.5 py-1 rounded-md text-xs font-medium ${getMRRStatusColor(
              rowData.status
            )}`}
          >
            {toProperCase(rowData.status)}
          </span>
        );
      case "priority":
        return (
          <span
            className={`px-1.5 py-1 rounded-md text-xs font-medium ${getPriorityStatusColor(
              rowData.priority
            )}`}
          >
            {toProperCase(rowData.priority)}
          </span>
        );
      case "actions":
        return (
          <div className="flex items-center gap-4">
            <FiInfo
              title="View Details"
              className="cursor-pointer text-primary hover:text-secondary text-lg"
              onClick={() =>
                navigate(VIEW_DETAILED_MRR.replace(":instanceId", rowData.id))
              }
            />

            <RoleValidator
              allowedRoles={[ROLES.PROJECT_ENGINEER, ROLES.SITE_ENGINEER]}
              mode="disable"
            >
              <button
                onClick={() =>
                  navigate(EDIT_MRR.replace(":instanceId", rowData.id))
                }
                disabled={
                  !(
                    rowData.status === MRR_STATUS.DRAFT &&
                    [ROLES.PROJECT_ENGINEER, ROLES.SITE_ENGINEER].includes(role)
                  )
                }
                className={`${
                  rowData.status === MRR_STATUS.DRAFT &&
                  [ROLES.PROJECT_ENGINEER, ROLES.SITE_ENGINEER].includes(role)
                    ? "text-blue-500"
                    : "text-gray-400 cursor-not-allowed"
                }`}
                title={`${
                  rowData.status === MRR_STATUS.DRAFT &&
                  [ROLES.PROJECT_ENGINEER, ROLES.SITE_ENGINEER].includes(role)
                    ? "Edit Details"
                    : `Cannot Edit in ${toProperCase(
                        rowData.status
                      )} state or insufficient permissions`
                }`}
              >
                <MdModeEditOutline className="h-4 w-4" />
              </button>
            </RoleValidator>

            {/* Delete Button */}
            <RoleValidator
              allowedRoles={[ROLES.PROJECT_ENGINEER, ROLES.SITE_ENGINEER]}
              mode="disable"
            >
              <button
                onClick={() => {
                  if (
                    window.confirm(
                      `Are you sure you want to delete MRR "${rowData.mrr_code}"? This action cannot be undone.`
                    )
                  ) {
                    handleDeleteMaterial(rowData);
                  }
                }}
                disabled={rowData.status !== MRR_STATUS.DRAFT}
                className={`${
                  rowData.status === MRR_STATUS.DRAFT
                    ? "text-red-500 hover:text-red-700"
                    : "text-gray-400 cursor-not-allowed"
                }`}
                title={
                  rowData.status === MRR_STATUS.DRAFT
                    ? "Remove"
                    : `Cannot delete in ${toProperCase(
                        rowData.status
                      )} state or insufficient permissions`
                }
              >
                <Trash2 size={18} />
              </button>
            </RoleValidator>
          </div>
        );
      default:
        return rowData[key];
    }
  };

  // Mobile Card View
  const renderMobileCards = () => (
    <ul className="space-y-3">
      {materials.length > 0 ? (
        materials.map((item) => {
          const canEdit =
            (item.status === MRR_STATUS.DRAFT &&
              [ROLES.PROJECT_ENGINEER, ROLES.SITE_ENGINEER].includes(role)) ||
            (item.status === MRR_STATUS.PENDING_FOR_APPROVAL &&
              role === ROLES.PROJECT_MANAGER);

          const canDelete =
            item.status === MRR_STATUS.DRAFT &&
            [ROLES.PROJECT_ENGINEER, ROLES.SITE_ENGINEER].includes(role);

          return (
            <li
              key={item.id}
              className="p-2 rounded-lg border text-sm flex justify-between shadow-sm"
              onClick={() =>
                navigate(VIEW_DETAILED_MRR.replace(":instanceId", item.id))
              }
            >
              <div className="max-w-[60%]">
                <div className="font-semibold text-gray-800 mb-1 truncate">
                  Warehouse {item.warehouse_name}
                </div>
                <div className="text-xs text-gray-600">
                  <span className="font-medium">MRR Code:</span> {item.mrr_code}
                </div>
              </div>
              <div className="flex flex-col items-end justify-between gap-2">
                <div className="flex items-center gap-4">
                  <button
                    disabled={!canEdit}
                    className={`${
                      canEdit
                        ? "text-blue-500"
                        : "text-gray-400 cursor-not-allowed"
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(EDIT_MRR.replace(":instanceId", item.id));
                    }}
                    title={
                      canEdit
                        ? "Edit Details"
                        : `Cannot Edit in ${toProperCase(
                            item.status
                          )} state or insufficient permissions`
                    }
                  >
                    <MdModeEditOutline className="h-4 w-4" />
                  </button>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (
                        window.confirm(
                          `Are you sure you want to delete MRR "${item.mrr_code}"? This action cannot be undone.`
                        )
                      ) {
                        handleDeleteMaterial(item);
                      }
                    }}
                    disabled={!canDelete}
                    className={`${
                      canDelete
                        ? "text-red-500 hover:text-red-700"
                        : "text-gray-400 cursor-not-allowed"
                    }`}
                    title={
                      canDelete
                        ? "Remove"
                        : `Cannot delete in ${toProperCase(
                            item.status
                          )} state or insufficient permissions`
                    }
                  >
                    <Trash2 size={14} />
                  </button>
                </div>

                <span
                  className={`px-1.5 py-1 rounded-md text-xs font-medium ${getMRRStatusColor(
                    item.status
                  )}`}
                >
                  {toProperCase(item.status)}
                </span>
              </div>
            </li>
          );
        })
      ) : (
        <div className="w-full h-[280px] flex justify-center items-center text-gray-300 flex-col">
          <ClipboardList size={35} />
          <span>No Material Requests</span>
        </div>
      )}
    </ul>
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      className="px-4 py-4"
    >
      <div className="flex justify-between items-center mb-4 mt-2 md:mt-0">
        <h2 className="text-xl font-semibold flex items-center gap-2">
          Material Requisition Requests
        </h2>

        {/* Desktop button */}
        <div className="hidden md:block">
          <RoleValidator
            allowedRoles={[ROLES.PROJECT_ENGINEER, ROLES.SITE_ENGINEER]}
            mode="disable"
          >
            <button
              onClick={() => navigate(CREATE_MRR)}
              className="bg-primary text-white text-sm px-3 py-2 rounded-md hover:bg-blue-700 transition-colors"
            >
              + Create MRR
            </button>
          </RoleValidator>
        </div>

        {/* Mobile button */}
        <div className="block md:hidden">
          <RoleValidator
            allowedRoles={[ROLES.PROJECT_ENGINEER, ROLES.SITE_ENGINEER]}
            mode="disable"
          >
            <button
              onClick={() => navigate(CREATE_MRR)}
              className="bg-primary text-white px-2 py-1 rounded-md hover:bg-[#bde0fe] max-h-fit"
              aria-label="Add MRR"
            >
              <MdAdd size={28} />
            </button>
          </RoleValidator>
        </div>
      </div>

      <div className="mb-4">
        <FilterCard
          tempFilters={filters}
          setTempFilters={setFilters}
          onFilterChange={(updated) => {
            setFilters(updated); // apply filters
          }}
          fields={[
            {
              key: "warehouse",
              label: "Warehouse",
              type: "select",
              options: warehouses,
            },
            {
              key: "status",
              label: "Status",
              type: "select",
              options: [
                { value: "", label: "All" },
                ...MRR_STATUS_VALUE_LABELS,
              ],
            },
            {
              key: "priority",
              label: "Priority",
              type: "select",
              options: [{ value: "", label: "All" }, ...PRIORITY_OPTIONS],
            },
          ]}
        />
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-[60vh]">
          <Spinner />
        </div>
      ) : (
        <div>
          {/* Desktop View */}
          <div className="hidden md:block h-[54vh] overflow-y-auto">
            <GenericTable
              tableColumns={MATERIAL_COLUMNS}
              tableRows={materials}
              columnLabels={MATERIAL_LABELS}
              renderField={renderFields}
            />
          </div>

          {/* Mobile View */}
          <div className="block md:hidden h-[64vh] overflow-y-auto">
            {renderMobileCards()}
          </div>

          {/* Pagination */}
          <Pagination
            page={page}
            rowsPerPage={rowsPerPage}
            totalPages={totalPages}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
          />
        </div>
      )}
    </motion.div>
  );
};

export default MaterialListPage;
